import { useEffect, useState } from "react";
import { FileSignature, Printer, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtNum, fmtDate } from "@/lib/api";
import { useSchema } from "@/context/SchemaContext";

const STATUSES = ["مسودة", "مرسل", "موقّع", "ملغي"];

export default function Contracts() {
  const { factories } = useSchema();
  const [list, setList] = useState([]);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ title: "", buyer: "", factory: "", price: "", currency: "SAR", terms: "" });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);

  const load = () => api.get("/r/contracts").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/r/sub_factories").then((r) => setSubs(r.data)).catch(() => {}); }, []);

  const products = [...Object.values(factories).filter((f) => f.id !== "main").map((f) => f.name), ...subs.map((s) => s.name)];

  const generate = async () => {
    if (!form.title || !form.buyer || !form.factory || !form.price) return toast.error("أكمل الحقول الأساسية");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/contracts/generate", { ...form, price: Number(form.price) });
      toast.success("تم توليد العقد"); setView(data); load();
      setForm({ title: "", buyer: "", factory: "", price: "", currency: "SAR", terms: "" });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setStatus = async (c, status) => { await api.put(`/r/contracts/${c.id}`, { data: { status } }); load(); if (view?.id === c.id) setView({ ...view, status }); };
  const remove = async (c) => { if (window.confirm("حذف العقد؟")) { await api.delete(`/r/contracts/${c.id}`); load(); } };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="contracts-page">
      <div>
        <p className="text-[11px] font-mono tracking-widest text-slate-500">الإدارة العامة</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-50">العقود والبيع</h1>
        <p className="text-sm text-slate-400 mt-1">تُباع المصانع كمنتجات مستقلة — بيع مرة واحدة مع نقل ملكية فكرية كاملة</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 panel p-5 space-y-4 self-start" data-testid="contract-form">
          <p className="font-semibold text-slate-100 flex items-center gap-2"><FileSignature size={16} className="text-red-300" /> توليد عقد بيع</p>
          <div className="space-y-1.5"><Label className="text-xs text-slate-400">عنوان العقد</Label><Input data-testid="contract-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-slate-900 border-slate-800" placeholder="بيع مصنع إدارة المطاعم" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-400">المشتري</Label><Input data-testid="contract-buyer-input" value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} className="bg-slate-900 border-slate-800" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">المصنع / المنتج</Label>
            <select data-testid="contract-factory-select" value={form.factory} onChange={(e) => setForm({ ...form, factory: e.target.value })} className="dark-select">
              <option value="">— اختر —</option>
              {products.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-1.5"><Label className="text-xs text-slate-400">السعر</Label><Input data-testid="contract-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-slate-900 border-slate-800" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">العملة</Label>
              <select data-testid="contract-currency-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="dark-select">{["SAR", "USD", "EUR", "AED"].map((c) => <option key={c}>{c}</option>)}</select></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-400">شروط إضافية</Label><Textarea data-testid="contract-terms-input" rows={3} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="bg-slate-900 border-slate-800" /></div>
          <Button data-testid="generate-contract-button" onClick={generate} disabled={busy} className="w-full bg-red-500 hover:bg-red-400 text-white font-semibold rounded-xl">{busy ? "جارٍ التوليد..." : "توليد العقد"}</Button>
        </div>

        <div className="lg:col-span-3 space-y-2" data-testid="contracts-list">
          {list.length === 0 && <div className="panel p-12 text-center text-slate-500 text-sm">لا عقود بعد</div>}
          {list.map((c) => (
            <div key={c.id} data-testid={`contract-card-${c.id}`} className="panel p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-slate-100">{c.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{c.buyer} · {c.factory} · <span className="font-mono">{fmtNum(c.price)} {c.currency}</span></p>
                <p className="text-[11px] text-slate-600 mt-0.5">{fmtDate(c.created_at)}</p>
              </div>
              <select data-testid={`contract-status-${c.id}`} value={c.status} onChange={(e) => setStatus(c, e.target.value)} className="dark-select !w-auto h-9">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              <button data-testid={`contract-view-${c.id}`} onClick={() => setView(c)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-slate-300 hover:bg-white/10"><Eye size={15} /></button>
              <button data-testid={`contract-delete-${c.id}`} onClick={() => remove(c)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="contract-view-dialog">
          <DialogHeader className="no-print"><DialogTitle className="text-right flex items-center justify-between">
            <span>{view?.title}</span>
            <Button data-testid="contract-print-button" size="sm" variant="secondary" onClick={() => window.print()}><Printer size={14} className="ml-1" /> طباعة</Button>
          </DialogTitle></DialogHeader>
          <pre className="print-area whitespace-pre-wrap font-[inherit] text-sm leading-8 text-slate-200 bg-white/[0.02] rounded-xl p-5 border border-white/[0.06]" data-testid="contract-content">{view?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
